import React, { forwardRef, useState } from 'react';
import type { FoundingCard } from '../services/foundingClass';

/**
 * The Founding Class card visual — authored in `em` so ONE component serves both the on-screen
 * preview (small font-size) and the 1080×1920 share capture (font-size 54px). 9:16 story format.
 * Light theme, reuses Lomi's citrus palette + the real logo (/logo192.png).
 */

const MONO_GRADIENTS = [
  'linear-gradient(135deg,#3c7a37,#5ba04f)',
  'linear-gradient(135deg,#2f8f9d,#4bb3c2)',
  'linear-gradient(135deg,#7a5bbd,#9a7bd6)',
  'linear-gradient(135deg,#c58a2e,#e0aa54)',
  'linear-gradient(135deg,#2f6fb0,#5a97d6)',
  'linear-gradient(135deg,#c47f7a,#dc9d94)',
];

function pillClass(h: { category: string; label: string }): 'gold' | 'leaf' | 'soft' {
  if (h.category === 'strongest') return 'soft';
  if (h.label.startsWith('Top ')) return 'leaf';
  return 'gold';
}

interface Props {
  card: FoundingCard;
}

const FoundingCardRender = forwardRef<HTMLDivElement, Props>(({ card }, ref) => {
  const [avatarBroken, setAvatarBroken] = useState(false);
  const initial = (card.display_name || 'L').trim().charAt(0).toUpperCase();
  const grad = MONO_GRADIENTS[initial.charCodeAt(0) % MONO_GRADIENTS.length];
  const hasFlex = card.highlights && card.highlights.length > 0;
  const showPhoto = !!card.avatar_url && !avatarBroken;

  return (
    <div ref={ref} className="fc-card">
      <div className="fc-brand">
        <img src="/logo192.png" alt="" className="fc-logo" /> Lomi <span className="fc-amh">ሎሚ</span>
      </div>

      {showPhoto ? (
        <img
          className="fc-av"
          src={card.avatar_url as string}
          alt=""
          crossOrigin="anonymous"
          onError={() => setAvatarBroken(true)}
        />
      ) : (
        <div className="fc-av fc-av-mono" style={{ background: grad }}>{initial}</div>
      )}

      <div className="fc-name">{card.display_name}</div>
      <div className="fc-class">Lomi Founding Class</div>
      <div className="fc-ec">Class of {card.class_year}</div>
      <div className="fc-rule" />

      {!hasFlex && <div className="fc-setline">One of the first 25,000 students</div>}

      <div className="fc-pills">
        {card.highlights.slice(0, 2).map((h, i) => (
          <div key={i} className={`fc-pill fc-${pillClass(h)}`}>{h.label}</div>
        ))}
      </div>

      <div className="fc-foot">
        <div className="fc-foot-t">The students who were here at the beginning</div>
        <div className="fc-foot-u"><span>lomistudy.com</span><span>·</span><span>@lomistudy</span></div>
      </div>
    </div>
  );
});

FoundingCardRender.displayName = 'FoundingCardRender';
export default FoundingCardRender;
